import { useState, useEffect, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import {
  Landmark,
  Plus,
  CheckCircle2,
  Shield,
  CreditCard,
  Trash2,
  AlertCircle,
  DollarSign,
  Clock,
  Zap,
  FileText,
  Phone,
  Mail,
  PhoneCall,
  Key,
  HelpCircle,
  RefreshCw,
  Copy,
  Lock,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard bank institutions
const STANDARD_INSTITUTIONS = [
  { id: "chase", name: "Chase", logo: "🏦" },
  { id: "bofa", name: "Bank of America", logo: "🏛️" },
  { id: "wells", name: "Wells Fargo", logo: "🏧" },
  { id: "citi", name: "Citibank", logo: "💳" },
  { id: "usbank", name: "US Bank", logo: "🏦" },
];

// MFA test institutions
const MFA_INSTITUTIONS = [
  { id: "ins_mfa_otp", name: "First Platypus Bank (OTP)", logo: "🔐", mfa_type: "device_otp", description: "Triggers Device OTP MFA" },
  { id: "ins_mfa_questions", name: "Tartan Bank (Questions)", logo: "❓", mfa_type: "security_questions", description: "Triggers Security Questions MFA" },
  { id: "ins_mfa_selections", name: "Houndstooth Bank (Selection)", logo: "📱", mfa_type: "mfa_selection", description: "Triggers MFA Selection" },
  { id: "ins_mfa_recaptcha", name: "Tattersall FCU (reCAPTCHA)", logo: "🤖", mfa_type: "recaptcha", description: "Triggers reCAPTCHA MFA" },
];

const BankAccount = () => {
  const { user, updateUser } = useAuth();
  const [bankAccount, setBankAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceNeedsRelink, setBalanceNeedsRelink] = useState(false);
  const [transactions, setTransactions] = useState(null);
  const [transactionsMessage, setTransactionsMessage] = useState(null);
  const [transactionsNeedsRelink, setTransactionsNeedsRelink] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [linkStep, setLinkStep] = useState(1);
  const [verificationMethod, setVerificationMethod] = useState("instant");
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [microDepositHint, setMicroDepositHint] = useState(null);
  const [institutionTab, setInstitutionTab] = useState("plaid");
  const [linkToken, setLinkToken] = useState(null);
  const [plaidEnv, setPlaidEnv] = useState("sandbox");

  // MFA State
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaStep, setMfaStep] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [securityAnswers, setSecurityAnswers] = useState(["", "", ""]);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState(null);

  const [formData, setFormData] = useState({
    account_name: "",
    mask: "",
    routing_number: "",
    account_number_last4: "",
  });
  const [verifyData, setVerifyData] = useState({
    amount1: "",
    amount2: "",
  });

  // Plaid Link success handler — handles new linking, update mode (same-day MD verification),
  // and the case where Plaid Link returns a publicToken for an already-linked item.
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setIsLinking(true);
    try {
      const userAlreadyHasBank = !!bankAccount;

      // Path A: Update mode (no public_token) OR user already has a bank → refresh verification.
      if (!publicToken || userAlreadyHasBank) {
        try {
          // If we DO have a publicToken AND a pre-existing bank, hit exchange-token first —
          // the backend will recognize the same item_id and refresh verification idempotently.
          if (publicToken && userAlreadyHasBank) {
            const account = (metadata && metadata.accounts && metadata.accounts[0]) || {};
            const institution = (metadata && metadata.institution) || {};
            const verificationStatus = account.verification_status || null;
            const verificationMethod = verificationStatus === "pending_manual_verification"
              ? "same_day_micro_deposits" : "instant";
            await axios.post(`${API_URL}/api/plaid/exchange-token`, {
              public_token: publicToken,
              account_name: account.name || bankAccount.account_name,
              institution_name: institution.name || bankAccount.institution_name,
              mask: account.mask || bankAccount.mask,
              verification_method: verificationMethod,
            });
          }
          const r = await axios.post(`${API_URL}/api/plaid/refresh-verification`);
          if (r.data?.is_verified) {
            toast.success("Bank verified via Plaid", { description: "Your bank is now ready for credit transfers" });
          } else {
            toast.success("Plaid update complete", { description: "Verification still pending — try again once the deposits show up" });
          }
        } catch (e) {
          toast.success("Plaid update complete", { description: "Refreshing your bank status…" });
        }
        await fetchBankAccount();
        setLinkToken(null);
        return;
      }

      // Path B: Fresh link (no existing bank) — exchange the public token to create the record.
      const account = (metadata && metadata.accounts && metadata.accounts[0]) || {};
      const institution = (metadata && metadata.institution) || {};
      const verificationStatus = account.verification_status || null;
      const verificationMethod = verificationStatus === "pending_manual_verification"
        ? "same_day_micro_deposits"
        : "instant";
      const response = await axios.post(`${API_URL}/api/plaid/exchange-token`, {
        public_token: publicToken,
        account_name: account.name || "Checking",
        institution_name: institution.name || "Bank",
        mask: account.mask || "0000",
        verification_method: verificationMethod,
      });

      setBankAccount(response.data);
      updateUser({ bank_account_linked: true });
      setLinkToken(null);
      if (verificationStatus === "pending_manual_verification") {
        toast.success("Bank linked — micro-deposits initiated", {
          description: "Plaid sent two small deposits. Verify in 1–2 business days using the button on this page.",
        });
      } else {
        toast.success("Bank account linked successfully via Plaid!");
      }
      fetchBankAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link account");
    } finally {
      setIsLinking(false);
    }
  }, [bankAccount]);

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.error("Plaid Link exited with error", err);
      }
      setLinkToken(null);
    },
  });

  // Auto-open Plaid Link when token is ready
  useEffect(() => {
    if (linkToken && plaidReady) {
      openPlaidLink();
    }
  }, [linkToken, plaidReady, openPlaidLink]);

  useEffect(() => {
    fetchBankAccount();
    axios.get(`${API_URL}/api/plaid/config`).then(r => setPlaidEnv(r.data.environment)).catch(() => {});
  }, []);

  const fetchBankAccount = async () => {
    try {
      const accountRes = await axios.get(`${API_URL}/api/plaid/bank-account`);
      setBankAccount(accountRes.data);

      // Balance — surface ITEM_LOGIN_REQUIRED so we can prompt re-auth instead of silent fail
      try {
        const balanceRes = await axios.get(`${API_URL}/api/plaid/balance`);
        setBalance(balanceRes.data);
        setBalanceNeedsRelink(false);
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail && typeof detail === "object" && detail.action === "relink_update") {
          setBalance(null);
          setBalanceNeedsRelink(true);
        } else {
          setBalance(null);
          setBalanceNeedsRelink(false);
        }
      }

      if (accountRes.data?.verification_status === "verified" && accountRes.data?.plaid_access_token_present) {
        fetchTransactions();
      }

      if (accountRes.data?.verification_status === "pending_micro_deposit") {
        try {
          const statusRes = await axios.get(`${API_URL}/api/plaid/micro-deposit/status/${accountRes.data.id}`);
          if (statusRes.data.sandbox_hint) {
            setMicroDepositHint(statusRes.data.sandbox_hint);
          }
        } catch (e) {
          console.error("Failed to fetch micro-deposit status");
        }
      }
    } catch (error) {
      setBankAccount(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/plaid/transactions?count=25`);
      if (res.data.is_ready === false) {
        setTransactions([]);
        setTransactionsMessage(res.data.message || "Transactions not yet available.");
        setTransactionsNeedsRelink(Boolean(res.data.needs_relink));
      } else {
        setTransactions(res.data.transactions || []);
        setTransactionsMessage(null);
        setTransactionsNeedsRelink(false);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns 409 with structured detail when ITEM_LOGIN_REQUIRED.
      if (error.response?.status === 409 && detail && typeof detail === "object" && detail.action === "relink_update") {
        setTransactions([]);
        setTransactionsMessage(detail.message || "Your bank requires re-authentication.");
        setTransactionsNeedsRelink(true);
      } else {
        setTransactions([]);
        setTransactionsMessage(typeof detail === "string" ? detail : "Unable to load transactions.");
        setTransactionsNeedsRelink(false);
      }
    } finally {
      setTransactionsLoading(false);
    }
  };


  const handleStartLink = async () => {
    // For Plaid Link tab - get a real link token
    if (institutionTab === "plaid") {
      setIsLinking(true);
      try {
        const res = await axios.post(`${API_URL}/api/plaid/link-token`);
        setLinkToken(res.data.link_token);
      } catch (error) {
        toast.error("Failed to initialize Plaid Link");
      } finally {
        setIsLinking(false);
      }
      return;
    }
    // For MFA demo tab - use the dialog flow
    try {
      await axios.post(`${API_URL}/api/plaid/link-token`);
      setLinkDialogOpen(true);
      setLinkStep(1);
      setVerificationMethod("instant");
      setMfaSession(null);
      setMfaChallenge(null);
      setMfaStep(null);
      setOtpCode("");
      setSecurityAnswers(["", "", ""]);
      setSelectedMfaMethod(null);
    } catch (error) {
      toast.error("Failed to start bank linking");
    }
  };

  const handleStartPlaidLink = async () => {
    setIsLinking(true);
    try {
      const res = await axios.post(`${API_URL}/api/plaid/link-token`);
      setLinkToken(res.data.link_token);
    } catch (error) {
      toast.error("Failed to initialize Plaid Link");
    } finally {
      setIsLinking(false);
    }
  };

  const handleStartPlaidLinkUpdate = async () => {
    // Re-launch Plaid Link in update mode — used to (a) complete same-day micro-deposit verification
    // once the deposits clear, or (b) repair an item whose login expired.
    setIsLinking(true);
    try {
      const res = await axios.post(`${API_URL}/api/plaid/link-token-update`);
      setLinkToken(res.data.link_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Plaid update");
    } finally {
      setIsLinking(false);
    }
  };

  const handleSandboxLink = async () => {
    setIsLinking(true);
    try {
      const res = await axios.post(`${API_URL}/api/plaid/sandbox/link-account`);
      setBankAccount(res.data);
      updateUser({ bank_account_linked: true });
      toast.success("Sandbox bank account linked! (First Platypus Bank)");
      fetchBankAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link sandbox account");
    } finally {
      setIsLinking(false);
    }
  };

  const handleStartMfaDemo = () => {
    setLinkDialogOpen(true);
    setLinkStep(1);
    setVerificationMethod("instant");
    setMfaSession(null);
    setMfaChallenge(null);
    setMfaStep(null);
    setOtpCode("");
    setSecurityAnswers(["", "", ""]);
    setSelectedMfaMethod(null);
  };

  const handleSelectInstitution = async (institution) => {
    setSelectedInstitution(institution);
    
    // Check if it's an MFA institution
    const isMfaInstitution = MFA_INSTITUTIONS.some(i => i.id === institution.id);
    
    if (isMfaInstitution) {
      // Initiate MFA session
      try {
        const response = await axios.post(`${API_URL}/api/plaid/mfa/initiate`, {
          institution_id: institution.id,
          institution_name: institution.name,
        });
        
        if (response.data.mfa_required) {
          setMfaSession(response.data);
          setMfaChallenge(response.data.challenge);
          
          // Set MFA step based on type
          if (response.data.mfa_type === "device_otp") {
            setMfaStep("otp");
          } else if (response.data.mfa_type === "security_questions") {
            setMfaStep("questions");
          } else if (response.data.mfa_type === "mfa_selection") {
            setMfaStep("selection");
          } else if (response.data.mfa_type === "recaptcha") {
            setMfaStep("recaptcha");
          }
          
          setLinkStep(4); // MFA step
        } else {
          setLinkStep(2);
        }
      } catch (error) {
        toast.error("Failed to initiate MFA session");
        setLinkStep(2);
      }
    } else {
      setLinkStep(2);
    }
  };

  const handleVerifyOTP = async () => {
    setIsLinking(true);
    try {
      const response = await axios.post(`${API_URL}/api/plaid/mfa/verify-otp`, {
        session_id: mfaSession.session_id,
        otp_code: otpCode,
      });
      
      if (response.data.success) {
        toast.success("OTP verified successfully!");
        setMfaStep("complete");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP code");
    } finally {
      setIsLinking(false);
    }
  };

  const handleVerifyQuestions = async () => {
    setIsLinking(true);
    try {
      const response = await axios.post(`${API_URL}/api/plaid/mfa/verify-questions`, {
        session_id: mfaSession.session_id,
        answers: securityAnswers,
      });
      
      if (response.data.success) {
        toast.success("Security questions verified!");
        setMfaStep("complete");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Incorrect answers");
    } finally {
      setIsLinking(false);
    }
  };

  const handleSelectMfaMethod = async (method) => {
    setIsLinking(true);
    try {
      const response = await axios.post(`${API_URL}/api/plaid/mfa/select-method`, {
        session_id: mfaSession.session_id,
        selected_method: method,
      });
      
      setSelectedMfaMethod(method);
      setMfaChallenge(response.data.challenge);
      setMfaStep("otp");
      toast.success(`Verification code sent via ${method}`);
    } catch (error) {
      toast.error("Failed to send verification code");
    } finally {
      setIsLinking(false);
    }
  };

  const handleVerifyRecaptcha = async () => {
    setIsLinking(true);
    try {
      // In sandbox, we just send a dummy token
      const response = await axios.post(`${API_URL}/api/plaid/mfa/verify-recaptcha`, {
        session_id: mfaSession.session_id,
        recaptcha_token: "sandbox_recaptcha_token",
      });
      
      if (response.data.success) {
        toast.success("reCAPTCHA verified!");
        setMfaStep("complete");
      }
    } catch (error) {
      toast.error("reCAPTCHA verification failed");
    } finally {
      setIsLinking(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await axios.post(`${API_URL}/api/plaid/mfa/resend-otp?session_id=${mfaSession.session_id}`);
      toast.success("OTP code resent!");
    } catch (error) {
      toast.error("Failed to resend OTP");
    }
  };

  const handleCompleteMfaLinking = async () => {
    if (!formData.account_name || !formData.mask) {
      toast.error("Pengagement fill in account details");
      return;
    }
    
    setIsLinking(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/plaid/mfa/complete-linking?session_id=${mfaSession.session_id}&account_name=${encodeURIComponent(formData.account_name)}&mask=${formData.mask}`
      );
      
      setBankAccount(response.data);
      updateUser({ bank_account_linked: true });
      setLinkDialogOpen(false);
      resetForm();
      toast.success("Bank account linked successfully!");
      fetchBankAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete linking");
    } finally {
      setIsLinking(false);
    }
  };

  const handleLinkAccount = async (e) => {
    e.preventDefault();
    setIsLinking(true);

    try {
      if (verificationMethod === "micro_deposit") {
        const response = await axios.post(`${API_URL}/api/plaid/micro-deposit/initiate`, {
          account_name: formData.account_name,
          institution_name: selectedInstitution.name,
          mask: formData.mask,
          routing_number: formData.routing_number,
          account_number_last4: formData.mask,
        });

        setBankAccount({
          ...response.data,
          account_type: "checking",
        });
        
        if (response.data.micro_deposit_amounts) {
          setMicroDepositHint({
            message: "SANDBOX ONLY: Use these amounts to verify",
            amounts: response.data.micro_deposit_amounts
          });
        }
        
        setLinkDialogOpen(false);
        resetForm();
        toast.success("Micro-deposits initiated! Check your bank in 1-2 business days.");
      } else {
        const response = await axios.post(`${API_URL}/api/plaid/exchange-token`, {
          public_token: `public-sandbox-${Date.now()}`,
          account_name: formData.account_name,
          institution_name: selectedInstitution.name,
          mask: formData.mask,
          verification_method: "instant",
        });

        setBankAccount(response.data);
        updateUser({ bank_account_linked: true });
        setLinkDialogOpen(false);
        resetForm();
        toast.success("Bank account linked and verified!");
        fetchBankAccount();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link account");
    } finally {
      setIsLinking(false);
    }
  };

  const handleVerifyMicroDeposit = async (e) => {
    e.preventDefault();
    setIsLinking(true);

    try {
      await axios.post(`${API_URL}/api/plaid/micro-deposit/verify`, {
        bank_account_id: bankAccount.id,
        amount1: parseFloat(verifyData.amount1),
        amount2: parseFloat(verifyData.amount2),
      });

      updateUser({ bank_account_linked: true });
      setVerifyDialogOpen(false);
      setVerifyData({ amount1: "", amount2: "" });
      setMicroDepositHint(null);
      toast.success("Bank account verified successfully!");
      fetchBankAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setIsLinking(false);
    }
  };

  const handleRemoveAccount = async () => {
    try {
      await axios.delete(`${API_URL}/api/plaid/bank-account`);
      setBankAccount(null);
      setBalance(null);
      setMicroDepositHint(null);
      updateUser({ bank_account_linked: false });
      toast.success("Bank account removed");
    } catch (error) {
      toast.error("Failed to remove bank account");
    }
  };

  const handleBackfillAccountId = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/plaid/backfill-account-id`);
      if (res.data?.status === "backfilled") {
        toast.success("Plaid account_id captured", {
          description: `Your bank is now ready for real Plaid credit transfers (${res.data.account_type})`,
        });
      } else if (res.data?.status === "already_set") {
        toast.success("Already Plaid-ready — no change needed");
      }
      fetchBankAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backfill failed — try removing and re-linking your bank");
    }
  };

  const resetForm = () => {
    setLinkStep(1);
    setSelectedInstitution(null);
    setVerificationMethod("instant");
    setMfaSession(null);
    setMfaChallenge(null);
    setMfaStep(null);
    setOtpCode("");
    setSecurityAnswers(["", "", ""]);
    setSelectedMfaMethod(null);
    setFormData({
      account_name: "",
      mask: "",
      routing_number: "",
      account_number_last4: "",
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getVerificationBadge = (status, method) => {
    if (status === "verified") {
      return (
        <Badge className="badge-completed">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Verified {method === "micro_deposit" ? "(Micro-Deposit)" : method === "mfa" ? "(MFA)" : "(Instant)"}
        </Badge>
      );
    }
    if (status === "pending_micro_deposit") {
      return (
        <Badge className="badge-pending">
          <Clock className="w-3 h-3 mr-1" />
          Pending Verification
        </Badge>
      );
    }
    if (status === "verification_failed") {
      return (
        <Badge className="badge-failed">
          <AlertCircle className="w-3 h-3 mr-1" />
          Verification Failed
        </Badge>
      );
    }
    return null;
  };

  // MFA Challenge Renderers
  const renderOtpChallenge = () => (
    <div className="space-y-4 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Key className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Enter Verification Code</h3>
        <p className="text-sm text-stone-600 mt-1">{mfaChallenge?.message}</p>
      </div>
      
      {mfaChallenge?.hint && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-emerald-800">{mfaChallenge.hint}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard("123456")}
              className="text-emerald-700"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>6-Digit Code</Label>
        <Input
          type="text"
          maxLength={6}
          placeholder="000000"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="text-center text-2xl tracking-widest"
          data-testid="mfa-otp-input"
        />
      </div>
      
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResendOTP}
          className="text-stone-600"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Resend Code
        </Button>
        <Button
          onClick={handleVerifyOTP}
          disabled={otpCode.length !== 6 || isLinking}
          className="bg-emerald-900 hover:bg-emerald-800"
          data-testid="verify-otp-btn"
        >
          {isLinking ? "Verifying..." : "Verify"}
        </Button>
      </div>
    </div>
  );

  const renderSecurityQuestions = () => (
    <div className="space-y-4 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <HelpCircle className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Security Questions</h3>
        <p className="text-sm text-stone-600 mt-1">{mfaChallenge?.message}</p>
      </div>
      
      {mfaChallenge?.hint && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800">{mfaChallenge.hint}</p>
        </div>
      )}
      
      <div className="space-y-4">
        {mfaChallenge?.questions?.map((question, index) => (
          <div key={index} className="space-y-2">
            <Label className="text-sm font-medium">{question}</Label>
            <Input
              type="text"
              placeholder="Your answer"
              value={securityAnswers[index]}
              onChange={(e) => {
                const newAnswers = [...securityAnswers];
                newAnswers[index] = e.target.value;
                setSecurityAnswers(newAnswers);
              }}
              data-testid={`security-answer-${index}`}
            />
          </div>
        ))}
      </div>
      
      <Button
        onClick={handleVerifyQuestions}
        disabled={securityAnswers.some(a => !a.trim()) || isLinking}
        className="w-full bg-emerald-900 hover:bg-emerald-800"
        data-testid="verify-questions-btn"
      >
        {isLinking ? "Verifying..." : "Submit Answers"}
      </Button>
    </div>
  );

  const renderMfaSelection = () => (
    <div className="space-y-4 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Phone className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Choose Verification Method</h3>
        <p className="text-sm text-stone-600 mt-1">{mfaChallenge?.message}</p>
      </div>
      
      {mfaChallenge?.hint && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800">{mfaChallenge.hint}</p>
        </div>
      )}
      
      <div className="space-y-2">
        {mfaChallenge?.options?.map((option) => (
          <button
            key={option.id}
            onClick={() => handleSelectMfaMethod(option.id)}
            disabled={isLinking}
            className="w-full p-4 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left flex items-center gap-4"
            data-testid={`mfa-option-${option.id}`}
          >
            <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
              {option.icon === "phone" && <Phone className="w-5 h-5 text-stone-600" />}
              {option.icon === "mail" && <Mail className="w-5 h-5 text-stone-600" />}
              {option.icon === "phone-call" && <PhoneCall className="w-5 h-5 text-stone-600" />}
            </div>
            <span className="font-medium text-stone-900">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderRecaptcha = () => (
    <div className="space-y-4 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Security Verification</h3>
        <p className="text-sm text-stone-600 mt-1">{mfaChallenge?.message}</p>
      </div>
      
      {mfaChallenge?.hint && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800">{mfaChallenge.hint}</p>
        </div>
      )}
      
      {/* Simulated reCAPTCHA box */}
      <div className="border-2 border-stone-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-stone-300 rounded flex items-center justify-center cursor-pointer hover:bg-emerald-50" onClick={handleVerifyRecaptcha}>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <span className="text-sm text-stone-700">I'm not a robot</span>
          <div className="ml-auto">
            <img src="https://www.gstatic.com/recaptcha/api2/logo_48.png" alt="reCAPTCHA" className="w-10 h-10 opacity-50" />
          </div>
        </div>
      </div>
      
      <Button
        onClick={handleVerifyRecaptcha}
        disabled={isLinking}
        className="w-full bg-emerald-900 hover:bg-emerald-800"
        data-testid="verify-recaptcha-btn"
      >
        {isLinking ? "Verifying..." : "Complete Verification"}
      </Button>
    </div>
  );

  const renderMfaComplete = () => (
    <div className="space-y-4 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">MFA Verified!</h3>
        <p className="text-sm text-stone-600 mt-1">Complete your account details to finish linking.</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Account Name</Label>
          <Input
            placeholder="e.g., Personal Checking"
            value={formData.account_name}
            onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
            data-testid="mfa-account-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Last 4 Digits</Label>
          <Input
            placeholder="1234"
            maxLength={4}
            value={formData.mask}
            onChange={(e) => setFormData(prev => ({ ...prev, mask: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
            data-testid="mfa-account-mask"
          />
        </div>
      </div>
      
      <Button
        onClick={handleCompleteMfaLinking}
        disabled={!formData.account_name || !formData.mask || isLinking}
        className="w-full bg-emerald-900 hover:bg-emerald-800"
        data-testid="complete-mfa-btn"
      >
        {isLinking ? "Linking..." : "Link Account"}
      </Button>
    </div>
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="bank-account-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bank Account
          </h1>
          <p className="text-stone-600 mt-1">
            Manage your linked bank account for payments.
          </p>
        </div>

        {/* Info Card */}
        <Card className={`${plaidEnv === 'production' ? 'border-emerald-200 bg-emerald-50' : 'border-blue-200 bg-blue-50'}`} data-testid="plaid-env-info">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className={`w-5 h-5 ${plaidEnv === 'production' ? 'text-emerald-600' : 'text-blue-600'} flex-shrink-0 mt-0.5`} />
              <div>
                <p className={`font-medium ${plaidEnv === 'production' ? 'text-emerald-900' : 'text-blue-900'}`}>
                  Plaid {plaidEnv === 'production' ? 'Production' : 'Sandbox'} Mode
                </p>
                <p className={`text-sm mt-1 ${plaidEnv === 'production' ? 'text-emerald-700' : 'text-blue-700'}`}>
                  {plaidEnv === 'production'
                    ? 'Connect Bank via Plaid opens the real Plaid Link UI to connect your actual bank account. Transfers use the live ACH/RTP network.'
                    : <>
                        <strong>Connect Bank via Plaid</strong> opens the real Plaid Link UI (sandbox credentials: <strong>user_good / pass_good</strong>).
                        <strong>Sandbox Quick Link</strong> instantly links a test bank (First Platypus Bank) via API.
                      </>
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {bankAccount ? (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Linked Account Card */}
            <Card data-testid="linked-account-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Linked Account</CardTitle>
                  {getVerificationBadge(bankAccount.verification_status, bankAccount.verification_method)}
                </div>
                <CardDescription>Your primary payment account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Landmark className="w-7 h-7 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-stone-900">{bankAccount.institution_name}</h3>
                    <p className="text-stone-600">{bankAccount.account_name}</p>
                    <p className="text-sm text-stone-500">
                      {bankAccount.account_type} •••• {bankAccount.mask}
                    </p>
                  </div>
                </div>

                {/* Plaid-ready backfill banner (legacy links missing plaid_account_id) */}
                {bankAccount.verification_status === "verified" && bankAccount.plaid_access_token_present && !bankAccount.plaid_account_id && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="plaid-ready-banner">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">One click to enable real Plaid credit transfers</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Your bank was linked before we captured the Plaid account_id. Click below to fetch it — your bank stays linked, and you'll be able to receive real Plaid credit transfers from CPC.
                        </p>
                        <Button
                          size="sm"
                          className="mt-3 bg-amber-600 hover:bg-amber-700"
                          onClick={handleBackfillAccountId}
                          data-testid="backfill-account-id-btn"
                        >
                          Make my bank Plaid-ready
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pending Micro-Deposit Verification — uses real Plaid Link in update mode */}
                {bankAccount.verification_status === "pending_micro_deposit" && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">Same-Day Micro-Deposit Verification Pending</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Plaid sent two small deposits to your bank. Check your bank statement in 1–2 business days, then re-open Plaid Link below to enter the amounts and complete verification.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-2 mt-3">
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={handleStartPlaidLinkUpdate}
                            disabled={isLinking}
                            data-testid="verify-deposits-plaid-btn"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            {isLinking ? "Loading Plaid…" : "Verify Deposits via Plaid"}
                          </Button>
                        </div>

                        {microDepositHint && Array.isArray(microDepositHint.amounts) && microDepositHint.amounts.length === 2 && (
                          <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                            <strong>🧪 Sandbox Testing:</strong> Use amounts ${microDepositHint.amounts[0]} and ${microDepositHint.amounts[1]}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-stone-100">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="remove-account-btn">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Bank Account</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove this bank account? You won't be able to make payments until you link a new one.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRemoveAccount} className="bg-red-600 hover:bg-red-700">
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>

            {/* Re-authentication banner — bank credentials expired (ITEM_LOGIN_REQUIRED) */}
            {bankAccount.verification_status === "verified" && balanceNeedsRelink && (
              <Card className="border-amber-300 bg-amber-50" data-testid="relink-banner">
                <CardContent className="py-4 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">
                        Your bank requires re-authentication
                      </p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        Plaid lost access to {bankAccount.institution_name || "your bank"}. Re-authenticate via Plaid Link to refresh balance and transactions — no need to re-link from scratch.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleStartPlaidLinkUpdate}
                    disabled={isLinking}
                    className="bg-amber-700 hover:bg-amber-800 text-white shrink-0"
                    data-testid="balance-relink-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {isLinking ? "Opening Plaid…" : "Re-authenticate"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Balance Card */}
            {bankAccount.verification_status === "verified" && balance && (
              <Card data-testid="balance-card">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Account Balance</CardTitle>
                      <CardDescription data-testid="balance-source-label">
                        {balance.is_live
                          ? `Live from Plaid · ${balance.institution || "Bank"}${balance.mask ? ` ····${balance.mask}` : ""}`
                          : "Cached balance"}
                      </CardDescription>
                    </div>
                    {balance.is_live && (
                      <span
                        data-testid="balance-live-badge"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                      >
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                        Live · {(balance.environment || "production").toUpperCase()}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-xl">
                      <p className="text-sm text-emerald-700">Available</p>
                      <p className="text-2xl font-bold text-emerald-900" data-testid="balance-available">
                        {formatCurrency(balance.available)}
                      </p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-xl">
                      <p className="text-sm text-stone-600">Current</p>
                      <p className="text-2xl font-bold text-stone-900" data-testid="balance-current">
                        {formatCurrency(balance.current)}
                      </p>
                    </div>
                  </div>
                  {balance.is_live ? (
                    <p className="text-xs text-stone-500 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-emerald-600" />
                      Balance fetched in real time from your bank via Plaid.
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Live Plaid connection unavailable — re-link your bank to refresh.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Transactions Card */}
            {bankAccount.verification_status === "verified" && (
              <Card data-testid="transactions-card">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Transactions</CardTitle>
                      <CardDescription>
                        Live activity from your linked bank via Plaid
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchTransactions}
                      disabled={transactionsLoading}
                      data-testid="transactions-refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1.5 ${transactionsLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {transactionsLoading && transactions === null ? (
                    <div className="text-sm text-stone-500 py-6 text-center" data-testid="transactions-loading">
                      Loading transactions…
                    </div>
                  ) : transactions && transactions.length > 0 ? (
                    <div className="divide-y divide-stone-100" data-testid="transactions-list">
                      {transactions.map((t) => (
                        <div
                          key={t.transaction_id}
                          className="flex items-center justify-between py-3 gap-4"
                          data-testid={`txn-row-${t.transaction_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-stone-900 truncate">
                              {t.merchant_name || t.name || "Transaction"}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-stone-500">{t.date || "—"}</span>
                              {t.pending && (
                                <span className="text-[10px] uppercase font-semibold tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                  Pending
                                </span>
                              )}
                              {t.category && t.category[0] && (
                                <span className="text-[10px] text-stone-500 truncate">· {t.category[0]}</span>
                              )}
                            </div>
                          </div>
                          <div className={`text-right shrink-0 font-semibold ${t.amount >= 0 ? "text-stone-900" : "text-emerald-700"}`}>
                            {/* Plaid convention: positive amount = debit (money out), negative = credit (money in) */}
                            {t.amount >= 0
                              ? `- ${formatCurrency(t.amount)}`
                              : `+ ${formatCurrency(Math.abs(t.amount))}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm py-6 text-center space-y-3" data-testid="transactions-empty">
                      <p className={transactionsNeedsRelink ? "text-amber-700" : "text-stone-500"}>
                        {transactionsMessage || "No transactions to display yet."}
                      </p>
                      {transactionsNeedsRelink && (
                        <Button
                          onClick={handleStartPlaidLinkUpdate}
                          disabled={isLinking}
                          className="bg-emerald-700 hover:bg-emerald-800"
                          data-testid="transactions-relink-btn"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {isLinking ? "Opening Plaid…" : "Re-authenticate Bank"}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card className="border-dashed" data-testid="no-account-card">
            <CardContent className="py-12">
              <div className="text-center">
                <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Landmark className="w-10 h-10 text-stone-400" />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-2">No Bank Account Linked</h3>
                <p className="text-stone-600 mb-6 max-w-md mx-auto">
                  Link your bank account to start making or receiving payments.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                  <Button
                    onClick={handleStartPlaidLink}
                    className="bg-emerald-900 hover:bg-emerald-800 rounded-full px-8"
                    disabled={isLinking}
                    data-testid="link-plaid-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {isLinking ? "Loading Plaid..." : "Connect Bank via Plaid"}
                  </Button>
                  {plaidEnv === "sandbox" && (
                    <Button
                      onClick={handleSandboxLink}
                      variant="outline"
                      className="rounded-full px-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      disabled={isLinking}
                      data-testid="sandbox-quick-link-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {isLinking ? "Linking..." : "Sandbox Quick Link"}
                    </Button>
                  )}
                </div>

                <p className="text-xs text-stone-500 max-w-md mx-auto" data-testid="plaid-link-helper">
                  Plaid Link handles all bank verification natively — instant verification, OAuth/MFA, and same-day micro-deposits — directly inside their secure widget.
                </p>

                <Dialog open={linkDialogOpen} onOpenChange={(open) => { setLinkDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {linkStep === 1 ? "Select Your Bank" : 
                         linkStep === 2 ? "Choose Verification Method" : 
                         linkStep === 3 ? "Enter Account Details" :
                         linkStep === 4 ? "Multi-Factor Authentication" : "Link Account"}
                      </DialogTitle>
                      <DialogDescription>
                        {linkStep === 1 ? "Choose your bank or test MFA institutions." :
                         linkStep === 2 ? "Select how you'd like to verify your account." :
                         linkStep === 3 ? "Provide your account details." :
                         linkStep === 4 ? "Complete the security verification." : ""}
                      </DialogDescription>
                    </DialogHeader>

                    {linkStep === 1 ? (
                      <div className="space-y-4 py-4">
                        <Tabs value={institutionTab} onValueChange={setInstitutionTab}>
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="standard">Standard Banks</TabsTrigger>
                            <TabsTrigger value="mfa">MFA Test Banks</TabsTrigger>
                          </TabsList>

                          <TabsContent value="standard" className="mt-4 space-y-2">
                            {STANDARD_INSTITUTIONS.map((institution) => (
                              <button
                                key={institution.id}
                                onClick={() => handleSelectInstitution(institution)}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left"
                                data-testid={`bank-${institution.id}`}
                              >
                                <span className="text-2xl">{institution.logo}</span>
                                <span className="font-medium text-stone-900">{institution.name}</span>
                              </button>
                            ))}
                          </TabsContent>
                          
                          <TabsContent value="mfa" className="mt-4 space-y-2">
                            <div className="p-3 bg-amber-50 rounded-lg mb-4">
                              <p className="text-sm text-amber-800">
                                <strong>Test Credentials:</strong> user_good / pass_good
                              </p>
                            </div>
                            {MFA_INSTITUTIONS.map((institution) => (
                              <button
                                key={institution.id}
                                onClick={() => handleSelectInstitution(institution)}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left"
                                data-testid={`mfa-bank-${institution.id}`}
                              >
                                <span className="text-2xl">{institution.logo}</span>
                                <div>
                                  <span className="font-medium text-stone-900 block">{institution.name}</span>
                                  <span className="text-xs text-stone-500">{institution.description}</span>
                                </div>
                              </button>
                            ))}
                          </TabsContent>
                        </Tabs>
                      </div>
                    ) : linkStep === 2 ? (
                      <div className="space-y-4 py-4">
                        <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg mb-4">
                          <span className="text-xl">{selectedInstitution?.logo}</span>
                          <span className="font-medium">{selectedInstitution?.name}</span>
                          <Button type="button" variant="ghost" size="sm" className="ml-auto text-stone-500" onClick={() => setLinkStep(1)}>
                            Change
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <button
                            onClick={() => { setVerificationMethod("instant"); setLinkStep(3); }}
                            className="w-full p-4 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                            data-testid="method-instant"
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Zap className="w-6 h-6 text-emerald-700" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-stone-900">Instant Verification</h4>
                                <p className="text-sm text-stone-600 mt-1">Connect securely using your bank credentials. Verified immediately.</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge className="bg-emerald-100 text-emerald-700">Recommended</Badge>
                                  <span className="text-xs text-stone-500">~30 seconds</span>
                                </div>
                              </div>
                            </div>
                          </button>

                          <button
                            onClick={() => { setVerificationMethod("micro_deposit"); setLinkStep(3); }}
                            className="w-full p-4 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left"
                            data-testid="method-micro-deposit"
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <FileText className="w-6 h-6 text-blue-700" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-stone-900">Micro-Deposit Verification</h4>
                                <p className="text-sm text-stone-600 mt-1">We'll send two small deposits to your account. Verify by entering the amounts.</p>
                                <span className="text-xs text-stone-500">1-2 business days</span>
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    ) : linkStep === 3 ? (
                      <form onSubmit={handleLinkAccount} className="space-y-4 py-4">
                        <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg mb-2">
                          <span className="text-xl">{selectedInstitution?.logo}</span>
                          <div>
                            <span className="font-medium">{selectedInstitution?.name}</span>
                            <p className="text-xs text-stone-500">
                              {verificationMethod === "instant" ? "Instant Verification" : "Micro-Deposit Verification"}
                            </p>
                          </div>
                          <Button type="button" variant="ghost" size="sm" className="ml-auto text-stone-500" onClick={() => setLinkStep(2)}>
                            Change
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label>Account Name</Label>
                          <Input
                            placeholder="e.g., Personal Checking"
                            value={formData.account_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
                            required
                            data-testid="account-name-input"
                          />
                        </div>

                        {verificationMethod === "micro_deposit" && (
                          <div className="space-y-2">
                            <Label>Routing Number</Label>
                            <Input
                              placeholder="9 digits"
                              maxLength={9}
                              value={formData.routing_number}
                              onChange={(e) => setFormData(prev => ({ ...prev, routing_number: e.target.value.replace(/\D/g, "").slice(0, 9) }))}
                              required
                              data-testid="routing-number-input"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>Last 4 Digits of Account</Label>
                          <Input
                            placeholder="1234"
                            maxLength={4}
                            value={formData.mask}
                            onChange={(e) => setFormData(prev => ({ ...prev, mask: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                            required
                            data-testid="account-mask-input"
                          />
                        </div>

                        <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg text-sm">
                          <Shield className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <p className="text-emerald-800">
                            {verificationMethod === "instant" 
                              ? "In production, Plaid securely handles bank authentication."
                              : "Two small deposits ($0.01-$0.99 each) will be sent to your account."}
                          </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                          <Button type="button" variant="outline" onClick={() => { setLinkDialogOpen(false); resetForm(); }}>
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" disabled={isLinking} data-testid="confirm-link-btn">
                            {isLinking ? "Processing..." : verificationMethod === "instant" ? "Link Account" : "Send Micro-Deposits"}
                          </Button>
                        </div>
                      </form>
                    ) : linkStep === 4 ? (
                      <div>
                        {mfaStep === "otp" && renderOtpChallenge()}
                        {mfaStep === "questions" && renderSecurityQuestions()}
                        {mfaStep === "selection" && renderMfaSelection()}
                        {mfaStep === "recaptcha" && renderRecaptcha()}
                        {mfaStep === "complete" && renderMfaComplete()}
                      </div>
                    ) : null}
                  </DialogContent>
                </Dialog>

                <div className="flex items-center justify-center gap-6 mt-8 text-sm text-stone-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Bank-level security
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Plaid MFA compliant
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Features Grid */}
        <div className="grid sm:grid-cols-4 gap-4">
          <Card className="bg-stone-50 border-stone-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-medium text-stone-900">Instant</p>
                <p className="text-xs text-stone-500">Verify in seconds</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-stone-50 border-stone-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="font-medium text-stone-900">Micro-Deposit</p>
                <p className="text-xs text-stone-500">Manual verify</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-stone-50 border-stone-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <p className="font-medium text-stone-900">MFA Support</p>
                <p className="text-xs text-stone-500">4 MFA types</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-stone-50 border-stone-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="font-medium text-stone-900">Multi-Rail</p>
                <p className="text-xs text-stone-500">ACH, RTP, FedNow</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default BankAccount;
